import {
  changePwdUrl,
  checkRegistrationEmailUrl,
  createPasswordUrl,
  resendActivationLinkByEmailUrl,
  resendEmailUrl,
  resetPasswordRequestUrl,
  resetPasswordUrl,
  saveOrUpdateLoyaltyMember,
  unsubscribeMembershipUrl,
  deleteMembershipUrl,
  uploadFileUrl,
  verifyRegistrationUrl
} from '../api/urls';
import { userConstants } from "../constants/user.constants";
import { getMembershipInfo } from './sandos4UAction';
import { notification } from 'antd';
import { store } from '../store';

export const REGISTRATION_EMAIL_STATUS = {
  NOT_FOUND: 'NOT_FOUND',
  INVALID_EMAIL: 'INVALID_EMAIL',
  PENDING_VERIFICATION: 'PENDING_VERIFICATION',
  UNSUBSCRIBED: 'UNSUBSCRIBED',
  ACTIVE: 'ACTIVE',
  BLOCKED: 'BLOCKED',
};

/**
 * Public pre-check for registration email status.
 * Body must be a JSON string (not an object), per backend contract.
 */
export function checkRegistrationEmail(email) {
  return () => {
    const normalizedEmail = (email || '').trim();
    return fetch(checkRegistrationEmailUrl, {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
      body: JSON.stringify(normalizedEmail),
    })
      .then((res) => res.json().then((data) => ({ res, data })))
      .then(({ res, data }) => {
        if (!res.ok || data.error !== 0 || !data.responseObj) {
          throw new Error(data.message || 'Error checking email');
        }
        return data.responseObj;
      });
  };
}

/**
 * Public resend of activation link by email (no JWT).
 * Body must be a JSON string (same pattern as checkRegistrationEmail).
 *
 * Backend currently allows PENDING_VERIFICATION only.
 * For UNSUBSCRIBED reactivation, member-service must accept that status on this
 * endpoint; GET /resendActivationLink/{id} returns 401 without JWT and is not usable anonymously.
 */
export function resendActivationLinkByEmail(email) {
  return () => {
    const normalizedEmail = (email || '').trim();
    return fetch(resendActivationLinkByEmailUrl, {
      method: 'POST',
      headers: new Headers({
        'Content-Type': 'application/json',
        Accept: 'application/json',
      }),
      body: JSON.stringify(normalizedEmail),
    })
      .then((res) => res.json().then((data) => ({ res, data })).catch(() => ({ res, data: null })))
      .then(({ res, data }) => {
        if (!res.ok || !data || data.error !== 0) {
          const err = new Error((data && data.message) || 'Error resending activation link');
          err.status = res.status;
          err.payload = data;
          throw err;
        }
        return data;
      });
  };
}

export function resendActivationLink(loyaltyMemberId, authToken) {
  return () => {
    const headers = {
      Accept: 'application/json',
    };
    if (authToken) {
      headers['X-Auth-Token'] = authToken;
    }

    return fetch(`${resendEmailUrl}${loyaltyMemberId}`, {
      method: 'GET',
      headers: new Headers(headers),
    }).then((res) => res.json());
  };
}

/**
 * Member-initiated temporary unsubscribe (status_id 9).
 * Authenticated POST with no body; member is taken from the JWT.
 */
export function unsubscribeMembership(authToken) {
  return () => {
    return fetch(unsubscribeMembershipUrl, {
      method: 'POST',
      headers: new Headers({
        'Content-type': 'application/json',
        Accept: 'application/json',
        'X-Auth-Token': authToken,
      }),
    })
      .then((res) => res.json().then((data) => ({ res, data })).catch(() => ({ res, data: null })))
      .then(({ res, data }) => {
        if (!res.ok || !data || data.error !== 0) {
          const err = new Error((data && data.message) || 'Error while unsubscribing membership');
          err.status = res.status;
          err.payload = data;
          throw err;
        }
        return data;
      });
  };
}

/**
 * Member-initiated hard delete. Authenticated POST with no body;
 * member is taken from the JWT.
 */
export function deleteMembership(authToken) {
  return () => {
    return fetch(deleteMembershipUrl, {
      method: 'POST',
      headers: new Headers({
        'Content-type': 'application/json',
        Accept: 'application/json',
        'X-Auth-Token': authToken,
      }),
    })
      .then((res) => res.json().then((data) => ({ res, data })).catch(() => ({ res, data: null })))
      .then(({ res, data }) => {
        if (!res.ok || !data || data.error !== 0) {
          const err = new Error((data && data.message) || 'Error while deleting membership');
          err.status = res.status;
          err.payload = data;
          throw err;
        }
        return data;
      });
  };
}

export function saveOrUpdateMember(authToken, userData) {
  return dispatch => (
    fetch(`${saveOrUpdateLoyaltyMember}`, {
      method: 'POST',
      headers: new Headers({
        'Content-type': 'application/json',
        'Accept': 'application/json',
        "X-Auth-Token": authToken
      }),
      body: JSON.stringify(userData)
    })
    .then(v => v.json())
    .then(r => {
      if (r.error) {
        notification.error({
          message: 'Sorry, an error has occurred when trying to save your profile. Please, try again later.',
        });
        return { success: false, error: null };
      }
      
      dispatch({ type: userConstants.LOY3_LOAD_DATA_SUCCESS, loy3ProfileData: r.responseObj })
      
      const locale = userData.preferredLanguage || userData.locale || 'en';
      getMembershipInfo(authToken, locale, userData.brandId)(dispatch);
      return { success: true };
    })
    .catch(error => ({ success: false, error })));
}


export function registerMember(authToken, data, successCallback, failureCallback) {
  return dispatch => {
    const headers = {
      'Content-type': 'application/json',
      'Accept': 'application/json',
    };
    if (authToken) {
      headers['X-Auth-Token'] = authToken;
    }

    fetch(`${saveOrUpdateLoyaltyMember}`, {
      method: 'POST',
      headers: new Headers(headers),
      body: JSON.stringify(data)
    })
      .then((res) => res.json().then((data) => ({ res, data })).catch(() => ({ res, data: null })))
      .then(({ res, data }) => {
        if (data && data.error === 0) {
          successCallback(data);
          dispatch({ type: userConstants.LOY3_REGISTER_SUCCESS, loy3RegisterData: data.responseObj });
          return;
        }

        const errorPayload = {
          status: res.status,
          error: data && data.error,
          message: (data && data.message) || 'Error saving the member data',
          responseObj: data ? data.responseObj : null,
        };
        failureCallback(errorPayload);
        dispatch({ type: userConstants.LOY3_REGISTER_FAILED, emailFailed: errorPayload });
      })
      .catch(() => {
        const errorPayload = {
          status: null,
          error: 1,
          message: 'Error saving the member data',
          responseObj: null,
        };
        failureCallback(errorPayload);
        dispatch({ type: userConstants.LOY3_REGISTER_FAILED, emailFailed: errorPayload });
      });
  }
}

export function verifyRegistration(authToken, verificationToken) {
  return dispatch => {
    fetch(`${verifyRegistrationUrl}${verificationToken}`, {
      method: 'GET',
      headers: new Headers({
        'Content-type': 'application/json',
        'Accept': 'application/json',
        "X-Auth-Token": authToken
      }),
    })
    .then(v => v.json())
    .then(r => {
      dispatch({ type: userConstants.LOY3_NEW_PASSWORD_SUCCESS, loy3NewPassword: r })
    });
  }
}

export function confirmPassword(authToken, data) {
  return dispatch => (
    fetch(`${createPasswordUrl}`, {
      method: 'POST',
      headers: new Headers({
        'Content-type': 'application/json',
        'Accept': 'application/json',
        "X-Auth-Token": authToken
      }),
      body: JSON.stringify(data)
    })
      .then(v => v.json())
      .then(r => r)
  )
}

export function resetPasswordRequest(email, brandId, locale) {
  return () => {
    return fetch(`${resetPasswordRequestUrl}?email=${email}&brandId=${brandId}&locale=${locale ? locale : 'en'}`, {
      method: 'GET',
      headers: new Headers({
        'Content-type': 'application/json',
        'Accept': 'application/json'
      })
    })
    .then(v => v.json());
  }
}

export function resetPassword(data, brandId) {
  console.log(data);
  return dispatch => (
    fetch(`${resetPasswordUrl}${data.val}/${data.password}?brandId=${brandId}`, {
      method: 'GET',
      headers: new Headers({
        'Content-type': 'application/json',
        'Accept': 'application/json'
      })
    }).then(v => v.json())
      .then(r => r)
  )
}

export function ChangePwd(authToken, data) {
  return dispatch => {
    fetch(`${changePwdUrl}`, {
      method: 'POST',
      headers: new Headers({
        'Content-type': 'application/json',
        'Accept': 'application/json',
        "X-Auth-Token": authToken
      }),
      body: JSON.stringify(data)
    })
      .then(v => v.json())
      .then(r => {
        dispatch({ type: userConstants.CHANGE_PWD_SUCCESS })
      })
  }
}

export function uploadImage(authToken, img) {
  const payload = new FormData();
  payload.append('image', img)
  return () => {
    return fetch(uploadFileUrl, {
      method: 'POST',
      headers: new Headers({
        "X-Auth-Token": authToken
      }),
      body: payload,
    })
      .then(v => v.json())
      .then((response) => {
        return { success: response.error === 0 };
      }).catch(() => ({ success: false }))
  }
}


/**
 * Method to subscribe the user email to the sandos CRM
 */
const subscribeToNewsletter = ({
  email,
  userName='',
  userSurname='',
  userLastname='',
  userCountryCode=null,
  userLanguage=null,
}) => () => {
  const defaultLang = store.getState().i18nState.lang;
  const spainCode = 'ES';
  const geoApiUrl = 'https://ip2c.org/self';
  const subscribeApiUrl = 'https://srcon.sandos.com:41443/SUscripcionCRM/Data/GetDataContractJson';
  const headers = new Headers({ 'Content-Type': 'application/json' });

  const performRequest = (countryCode, languageCode) => {
    const payload = {
      'email': email,
      'nombre': userName,
      'apellido1': userSurname,
      'apellido2': userLastname,
      'marketingList': '889bdf8a-8436-eb11-a813-000d3a4b2c9e',
      'origen': '6c4ace9f-ab72-eb11-a812-000d3ac2332e',
      'pais': countryCode,
      'idioma': languageCode,
    };

    return fetch(subscribeApiUrl, {
      method: 'post',
      headers,
      body: JSON.stringify(payload),
    });
  }

  if (userCountryCode && userLanguage) {
    return performRequest(userCountryCode, userLanguage);
  }

  if (userCountryCode && !userLanguage) {
    return performRequest(userCountryCode, defaultLang);
  }

  // Firstly, we need to detect the country using the api suggested by Sandos.
  return fetch(geoApiUrl)
    .then((geoResponse) => {
      if (geoResponse.ok) {
        return geoResponse.text().then((geoData) => {
          const countryCode = geoData.split(';')[1];
          return performRequest(countryCode, defaultLang);
        });
      }
      return performRequest(spainCode, defaultLang);
    })
    .catch(() => {
      return performRequest(spainCode, defaultLang);
    });

};

export {
  subscribeToNewsletter,
};

